import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle, AlertTriangle, Info, X } from "lucide-react";

interface ToastProps {
  type: "success" | "error" | "info";
  message: string;
  onClose: () => void;
  duration?: number;
}

const combineClasses = (...classes: (string | boolean | undefined)[]) => {
  return classes.filter(Boolean).join(" ");
};

const MessageToast: React.FC<ToastProps> = ({
  type,
  message,
  onClose,
  duration = 3500,
}) => {
  const [visible, setVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(() => {
      setIsLeaving(true);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(onClose, 300);
  };

  return createPortal(
    <div
      className={combineClasses(
        "fixed bottom-10 right-6 z-[300] transform",
        "transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1.0)]",
        visible && !isLeaving
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-4"
      )}
      role="alert"
      aria-live="polite"
    >
      <div
        className={combineClasses(
          "flex items-start gap-3 px-4 py-3 rounded-lg",
          "shadow-md shadow-black/15 backdrop-blur-md",
          "max-w-[480px] border-l-4",
          "bg-white text-black dark:bg-[#1c1c1e] dark:text-white",
          type === "success"
            ? "border-green-500"
            : type === "info"
            ? "border-yellow"
            : "border-red-500"
        )}
      >
        {type === "success" ? (
          <CheckCircle className="w-5 h-5 flex-shrink-0 text-green-500 mt-0.5" />
        ) : type === "info" ? (
          <Info className="w-5 h-5 flex-shrink-0 text-yellow mt-0.5" />
        ) : (
          <AlertTriangle className="w-5 h-5 flex-shrink-0 text-red-500 mt-0.5" />
        )}

        <p className="text-sm font-medium leading-relaxed break-words flex-1">
          {message}
        </p>

        <button
          onClick={handleClose}
          className={combineClasses(
            "ml-auto p-1 rounded-full flex-shrink-0",
            "transition-colors duration-200",
            "hover:bg-gray-100 focus:bg-gray-100 dark:hover:bg-white/10 dark:focus:bg-white/10",
            "focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-white/50"
          )}
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default MessageToast;
